// components/modals/PromocionCuponModal.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Copy, Tag, Calendar, Check } from 'lucide-react';

const PromocionCuponModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(isOpen);
  const cuponCode = 'InKayros2025';

  useEffect(() => {
    setShowModal(isOpen);
  }, [isOpen]);

  // Comprobar si el usuario ya ha visto este modal hoy
  useEffect(() => {
    if (isOpen) {
      const lastSeen = localStorage.getItem('promoModalLastSeen');
      const today = new Date().toISOString().split('T')[0];
      
      if (lastSeen === today) {
        // Ya visto hoy, no mostrar
        setShowModal(false);
        if (onClose) onClose();
      } else {
        // No visto hoy, mostrar y guardar fecha
        setShowModal(true);
        localStorage.setItem('promoModalLastSeen', today);
      }
    }
  }, [isOpen, onClose]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(cuponCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  if (!showModal) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full"
        >
          {/* Banner superior */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 h-24 flex items-center justify-center relative">
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="absolute -bottom-8 bg-white rounded-full p-4 shadow-lg"
            >
              <Gift className="w-10 h-10 text-indigo-600" />
            </motion.div>
            <button 
              onClick={handleClose}
              className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Contenido */}
          <div className="pt-12 px-6 pb-6 text-center">
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-gray-800 mb-2"
            >
              ¡Oferta Especial!
            </motion.h2>
            <motion.p 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-600 mb-4"
            >
              Obtén <span className="font-bold text-indigo-600">3 meses GRATIS</span> en el Plan Casual
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center mb-4 justify-center space-x-2 text-sm text-gray-600"
            >
              <div className="flex items-center">
                <Tag className="w-4 h-4 mr-1" />
                <span>Plan Casual</span>
              </div>
              <span>•</span>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <span>Válido hasta 31/07/2025</span>
              </div>
            </motion.div>

            {/* Código de cupón */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-gray-100 p-4 rounded-lg mb-6 relative"
            >
              <p className="text-xs text-gray-500 mb-2">Tu código de cupón:</p>
              <div className="flex items-center justify-between">
                <span className="text-xl font-mono font-bold text-indigo-700">{cuponCode}</span>
                <button 
                  onClick={handleCopyCode}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 p-2 rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              {copied && (
                <span className="absolute -bottom-5 right-0 text-xs text-green-600 font-medium">
                  ¡Copiado al portapapeles!
                </span>
              )}
            </motion.div>

            {/* Botones */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex space-x-3"
            >
              <button
                onClick={handleClose}
                className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
              >
                Continuar navegando
              </button>
              <button
                onClick={() => {
                  handleClose();
                  window.location.href = '/planes'; // Redirigir a la página de planes
                }}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-colors"
              >
                Ver planes
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PromocionCuponModal;